import { quoteFromSnapshot, displayModel, SOURCE_PAGE, nextRomeMidnightMs } from './annarita-gold-indicator.js';
export const GOLD_PRICING_TAG = 'oro-quotazione-dinamica';
export function isGoldSpotContext(root) {
  return root.dataset.goldContext === 'product' && root.dataset.goldPricingTag === GOLD_PRICING_TAG ||
    root.dataset.goldContext === 'collection' && root.dataset.collectionHandle === 'oro';
}

export function readInlineSnapshot(root) {
  const template = root.querySelector('template[data-gold-snapshot]');
  const text = template?.content?.textContent;
  if (typeof text !== 'string' || text.length > 100000) throw new Error('Missing quote snapshot');
  return JSON.parse(text);
}

export function sectionRequestUrl(productUrl, sectionId, origin) {
  if (typeof sectionId !== 'string' || !/^[A-Za-z0-9_-]{1,192}$/.test(sectionId)) {
    throw new Error('Invalid section ID');
  }
  const url = new URL(productUrl, origin);
  if (url.origin !== origin || !/^\/(?:[a-z]{2}(?:-[A-Za-z]{2})?\/)?(?:products\/[^/]+|collections\/oro)$/.test(url.pathname)) throw new Error('Invalid gold resource URL');
  url.search = '';
  url.hash = '';
  url.searchParams.set('section_id', sectionId);
  return url.toString();
}

export function cloudRequestUrl(endpoint) {
  if (typeof endpoint !== 'string' || endpoint.length > 2048) throw new Error('Invalid cloud endpoint');
  const url = new URL(endpoint);
  if (url.protocol !== 'https:' || url.pathname !== '/gold' || url.username || url.password || url.search || url.hash) {
    throw new Error('Cloud endpoint must be an explicit public HTTPS /gold URL');
  }
  return url.toString();
}

function snapshotFromSection(html, sectionId) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const match = [...doc.querySelectorAll('ag-gold-spot[data-section-id]')].find(
    node => node.dataset.sectionId === sectionId && isGoldSpotContext(node)
  );
  if (!match) throw new Error('Gold quote missing from rendered section');
  return readInlineSnapshot(match);
}

export function mountShopifyGoldSpot(root, {
  fetcher = globalThis.fetch, origin = globalThis.location?.origin,
  clock = () => Date.now(), schedule = setTimeout, unschedule = clearTimeout,
  extractSnapshot = snapshotFromSection
} = {}) {
  if (!isGoldSpotContext(root)) return { mounted: false };
  let quote;
  let stopped = false;
  let dayTimer;
  let dayBoundaryMs;
  let freshnessTimer;
  let retryTimer;
  let retryCount = 0;
  let inFlight = false;
  let readFailed = false;
  const el = name => root.querySelector(`[data-gold-${name}]`);
  const put = (name, value) => { if (el(name)) el(name).textContent = value; };
  const label = key => root.dataset[key] || '';
  const source = el('source');
  if (source) { source.href = SOURCE_PAGE; source.rel = 'noopener'; }

  function adopt(snapshot) {
    const next = quoteFromSnapshot(snapshot);
    if (next.sourceAsOfMs < 946684800000 || next.sourceAsOfMs > clock() + 60000) {
      throw new Error('Invalid source timestamp');
    }
    if (next.daily && next.validFromMs > clock()) throw new Error('Future daily quote');
    if (quote && next.sourceAsOfMs < quote.sourceAsOfMs) throw new Error('Source snapshot moved backwards');
    quote = next;
    readFailed = false;
  }

  function render() {
    if (!quote) {
      put('status', label('labelUnavailable'));
      put('price', '—'); put('18kt', '—'); put('9kt', '—');
      put('asof', ''); put('validity', '');
      root.dataset.quoteState = 'unavailable';
      return;
    }
    const model = displayModel(quote, clock());
    put('status', readFailed || model.stale ? label('labelStale') : label('labelFresh'));
    put('price', model.fineGold); put('18kt', model.theoretical18kt); put('9kt', model.theoretical9kt);
    put('asof', `${label('labelAsof')} ${model.asOf}`);
    put('validity', model.validUntil ? `${label('labelValidUntil')} ${model.validUntil}` : '');
    put('note', label('labelNote'));
    root.dataset.quoteState = readFailed || model.stale ? 'last-known' : 'indicative';
  }
  function offerRetry(seconds) {
    if (Number.isInteger(seconds) && seconds >= 1 && seconds <= 300 && retryTimer === undefined && retryCount < 1) {
      retryCount += 1;
      retryTimer = schedule(() => { retryTimer = undefined; refresh(); }, seconds * 1000);
    }
  }

  async function refresh() {
    if (stopped || inFlight || !root.dataset.cloudEndpoint && !root.dataset.sectionId) return { refreshed: false };
    inFlight = true;
    try {
      let snapshot;
      let cloudStatus = 'current';
      if (root.dataset.cloudEndpoint) {
        const url = cloudRequestUrl(root.dataset.cloudEndpoint);
        const response = await fetcher(url, { credentials: 'omit', mode: 'cors', cache: 'no-store', referrerPolicy: 'no-referrer' });
        if ((!response.ok && response.status !== 503) || response.type === 'opaque') throw new Error('Cloud quote unavailable');
        if (response.url !== url) throw new Error('Unexpected cloud redirect');
        const text = await response.text();
        if (text.length > 100000) throw new Error('Excessive cloud quote response');
        const body = JSON.parse(text);
        if (body.available !== true || !body.quote) {
          if (response.status === 503 && ['stale', 'initializing'].includes(body.status)) {
            offerRetry(body.retry_after_seconds ?? (body.status === 'initializing' ? 65 : null));
          }
          throw new Error('Cloud daily quote not available');
        }
        snapshot = body.quote;
        cloudStatus = body.status;
        if (body.status === 'updating') offerRetry(body.retry_after_seconds);
      } else {
        const url = sectionRequestUrl(root.dataset.productUrl, root.dataset.sectionId, origin);
        const response = await fetcher(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok || response.type === 'opaque') throw new Error('Section unavailable');
        const final = new URL(response.url);
        if (final.origin !== origin || final.pathname !== new URL(url).pathname) throw new Error('Unexpected section redirect');
        const html = await response.text();
        if (html.length > 2000000) throw new Error('Excessive section response');
        snapshot = extractSnapshot(html, root.dataset.sectionId);
      }
      if (stopped) return { refreshed: false };
      adopt(snapshot);
      if (cloudStatus !== 'current') readFailed = true;
      render();
      armDayBoundary();
      return { refreshed: true, sourceAsOfMs: quote.sourceAsOfMs };
    } catch (error) {
      readFailed = true;
      if (!stopped) render();
      return { refreshed: false, reason: 'section unavailable; original timestamp preserved' };
    } finally {
      inFlight = false;
    }
  }

  function armDayBoundary() {
    if (stopped) return;
    const now = clock();
    const boundary = quote?.daily && quote.validUntilMs > now ? quote.validUntilMs : nextRomeMidnightMs(now);
    if (dayTimer !== undefined && dayBoundaryMs === boundary) return;
    if (dayTimer !== undefined) unschedule(dayTimer);
    dayBoundaryMs = boundary;
    dayTimer = schedule(daily, Math.max(1000, boundary - now + 2000));
  }
  async function daily() {
    if (stopped) return;
    dayTimer = undefined;
    retryCount = 0;
    await refresh();
    armDayBoundary();
  }

  function checkFreshness() {
    if (stopped) return;
    render();
    freshnessTimer = schedule(checkFreshness, 60000);
  }

  try { adopt(readInlineSnapshot(root)); } catch (error) { readFailed = true; }
  render();
  if (root.dataset.cloudEndpoint) refresh();
  armDayBoundary();
  freshnessTimer = schedule(checkFreshness, 60000);
  return {
    mounted: true, refresh, render,
    destroy() {
      stopped = true;
      if (dayTimer !== undefined) unschedule(dayTimer);
      if (freshnessTimer !== undefined) unschedule(freshnessTimer);
      if (retryTimer !== undefined) unschedule(retryTimer);
    }
  };
}

if (globalThis.HTMLElement && globalThis.customElements && !customElements.get('ag-gold-spot')) {
  class AnnaritaGoldSpot extends HTMLElement {
    connectedCallback() {
      this.goldIndicator?.destroy();
      this.goldIndicator = mountShopifyGoldSpot(this);
    }
    disconnectedCallback() { this.goldIndicator?.destroy(); }
  }
  customElements.define('ag-gold-spot', AnnaritaGoldSpot);
}
